import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { DateTokenNodeView } from "@/components/editor/nodeviews/DateTokenNodeView";

/**
 * Inline date token — the *body* form of a date (print today's date or a
 * fixed day in running text). Distinct from the Title block's document
 * `\date{…}` which only lives in the preamble + `\maketitle`.
 *
 * Serialises as:
 *   mode "today" → `\today`
 *   mode "fixed" → the human/ISO date text (never body `\date{…}` —
 *   that command is preamble-only in LaTeX and does nothing mid-paragraph).
 *
 * Parses both `\today` and legacy/AI `\date{\today}` / `\date{…}` so
 * Architect edits that write preamble commands into body still render.
 */
export const DateToken = Node.create({
  name: "dateToken",
  group: "inline",
  inline: true,
  atom: true,

  addAttributes() {
    return {
      /** "today" | "fixed" */
      mode: {
        default: "today",
        parseHTML: (el) => el.getAttribute("data-mode") || "today",
        renderHTML: (attrs) => ({ "data-mode": attrs.mode }),
      },
      /** ISO yyyy-mm-dd or free text when mode is fixed; ignored for today. */
      value: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-value") || "",
        renderHTML: (attrs) =>
          attrs.value ? { "data-value": attrs.value } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-type="date-token"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, { "data-type": "date-token" }),
    ];
  },

  addNodeView() {
    // Only intercept mousedown so PM doesn't select-only on first click.
    // Do NOT stop all clicks — that breaks Save/Cancel on the portaled modal.
    return ReactNodeViewRenderer(DateTokenNodeView, {
      stopEvent: ({ event }) =>
        event.type === "mousedown" || event.type === "pointerdown",
    });
  },
});
